import { type ReactNode, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  type TierDraft,
  cascadeUpdate,
  insertTier,
  removeTier,
} from '../utils/group-tier-helpers';
import type { TierFieldErrors } from '../utils/validate-group-tiers';
import { GroupTierRows } from './group-tier-rows';

type Props = {
  tiers: TierDraft[];
  errors: TierFieldErrors;
  onChange: (t: TierDraft[]) => void;
};

function Header(): ReactNode {
  const cls = 'text-[10px] font-medium uppercase text-text-tertiary/60';
  return (
    <div className="flex items-center gap-sm px-1 pb-1">
      <span className="w-[14px]" />
      <span className={`${cls} w-[140px]`}>Rango media diaria</span>
      <span className={cls}>$ / persona</span>
    </div>
  );
}

function useReorder(
  tiers: TierDraft[],
  onChange: (t: TierDraft[]) => void
): (e: DragEndEvent) => void {
  const last = tiers.length - 1;
  return useCallback(
    (e) => {
      if (!e.over || e.active.id === e.over.id) return;
      const ids = tiers.map((t) => t.id);
      const from = ids.indexOf(e.active.id as string);
      const toRaw = ids.indexOf(e.over.id as string);
      const to = Math.max(1, Math.min(last - 1, toRaw));
      if (from < 1 || from > last - 1) return;
      onChange(arrayMove(tiers, from, to));
    },
    [tiers, last, onChange]
  );
}

function useDndSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
}

function DndList(p: Props): ReactNode {
  const sensors = useDndSensors();
  const onReorder = useReorder(p.tiers, p.onChange);
  const ids = p.tiers.map((t) => t.id);
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onReorder}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <GroupTierRows
          tiers={p.tiers}
          errors={p.errors}
          onUpdate={(i, t) => p.onChange(cascadeUpdate(p.tiers, i, t))}
          onRemove={(i) => p.onChange(removeTier(p.tiers, i))}
        />
      </SortableContext>
    </DndContext>
  );
}

export function GroupTierEditor(p: Props): ReactNode {
  return (
    <div>
      <Header />
      <DndList {...p} />
      <Button
        variant="ghost"
        size="sm"
        onClick={() => p.onChange(insertTier(p.tiers))}
        className="mt-sm gap-1"
      >
        <Plus size={14} /> Añadir tramo
      </Button>
    </div>
  );
}
